import type { DnsRecord } from '../api';
import { BulbIcon } from './icons';
import { CopyButton, Status } from './ui';

/**
 * What to type in a DNS provider's Name/Host field. The API gives it relative to the registrable
 * domain (`vx-bounces.test` for test.acme.in); older responses fall back to the added domain.
 */
export function hostPart(record: DnsRecord, domain: string): string {
  if (record.host) return record.host;
  if (record.name === domain) return '@';
  return record.name.endsWith(`.${domain}`)
    ? record.name.slice(0, -(domain.length + 1))
    : record.name;
}

function purpose(record: DnsRecord): string {
  if (record.name.startsWith('_dmarc.')) return 'DMARC (recommended)';
  if (record.type === 'CNAME') return 'DKIM signing key';
  if (record.name.includes('._domainkey.')) return 'DKIM signing key (public)';
  if (record.type === 'MX') return 'Return path for bounces';
  return 'SPF for the return path';
}

/** DKIM public keys run to about 410 characters: wrap them and keep the copy button at the top. */
const LONG_VALUE = 100;

/** The DNS records for a domain, with copy buttons and per-record status. */
export function DnsRecords({ domain, records }: { domain: string; records: DnsRecord[] }) {
  // The domain's own MAIL FROM label: vx-bounces, vx-bounces.test on a subdomain, send on older domains.
  const mx = records.find((record) => record.type === 'MX');
  const example = mx ? hostPart(mx, domain) : 'vx-bounces';
  const exampleFull = mx?.name ?? `vx-bounces.${domain}`;
  const hasLongValue = records.some((record) => record.value.length > LONG_VALUE);
  const copyable = (record: DnsRecord) =>
    record.value.length > LONG_VALUE ? 'copyable long' : 'copyable';
  return (
    <>
      <div className="dns-cards show-sm">
        {records.map((record) => (
          <div key={`${record.type}-${record.name}`} className="dns-card">
            <div className="dns-card-top">
              <span className="chip mono">{record.type}</span>
              <span className="dim">{purpose(record)}</span>
              {record.status ? (
                <Status status={record.status} />
              ) : (
                <span className="dim">Optional</span>
              )}
            </div>
            <div className="dns-row">
              <span className="dim">Name</span>
              <span className="copyable">
                <code className="dns-value" title={record.name}>
                  {hostPart(record, domain)}
                </code>
                <CopyButton value={hostPart(record, domain)} label="Copy name" />
              </span>
            </div>
            <div className="dns-row">
              <span className="dim">Full</span>
              <span className="full-name">{record.name}</span>
            </div>
            <div className="dns-row">
              <span className="dim">Value</span>
              <span className={copyable(record)}>
                <code className="dns-value">
                  {record.priority ? `${record.priority} ` : ''}
                  {record.value}
                </code>
                <CopyButton value={record.value} label="Copy value" />
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="table-wrap hide-sm">
        <table className="table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Name</th>
              <th>Value</th>
              <th className="nowrap hide-sm">Priority</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={`${record.type}-${record.name}`}>
                <td>
                  <span className="chip mono">{record.type}</span>
                </td>
                <td>
                  <span className="copyable">
                    <code title={record.name}>{hostPart(record, domain)}</code>
                    <CopyButton value={hostPart(record, domain)} label="Copy name" />
                  </span>
                  <span className="sub">{purpose(record)}</span>
                  <span className="sub full-name">Full name: {record.name}</span>
                </td>
                <td style={{ maxWidth: 380 }}>
                  <span className={copyable(record)}>
                    <code className="dns-value">{record.value}</code>
                    <CopyButton value={record.value} label="Copy value" />
                  </span>
                </td>
                <td className="mono hide-sm">{record.priority ?? '—'}</td>
                <td>
                  {record.status ? (
                    <Status status={record.status} />
                  ) : (
                    <span className="dim">Optional</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="tip">
        <BulbIcon />
        <span>
          In your DNS provider's <strong>Name</strong> or <strong>Host</strong> field, paste only
          the part shown here (for example <code>{example}</code>, not <code>{exampleFull}</code>
          ). On Cloudflare, set every record to <strong>DNS only</strong> (grey cloud).
          {hasLongValue
            ? ' Paste the long DKIM value as one string: Cloudflare and most providers accept it as it is.'
            : ''}{' '}
          Already have a DMARC record? Keep yours.{' '}
          <a
            className="link"
            href="https://vaartix.com/docs/guides/dns-providers/"
            target="_blank"
            rel="noreferrer"
          >
            Step-by-step guides
          </a>
        </span>
      </div>
    </>
  );
}
